import type { DataFlowBuilder } from '@/engine/DataFlow';
import * as DT from './TTVRDataTypes';
import * as Algo from './TTVRAlgorithmHelpers';
import * as THREE from 'three';
import {
  buildAreaMeasurement,
  buildDeliveryAlgorithm,
  buildPointToPlaneDistanceMeasurementSigned,
  buildPointToPlaneDistanceMeasurementSignedInv,
  buildPointToPointDistanceMeasurement
} from '../common/CardiacAlgorithmBuilderCommon';

export function buildAlgorithm(builder: DataFlowBuilder): void {
  builder.name('instrumentation.path').define(new DT.StringT('instr/ttvr'));

  builder
    .name('instrumentation')
    .bind(Algo.loadInstrumentationLibraryAsync, DT.InstrumentationLibrary, 'instrumentation.path');

  builder.name('right_atrium.descriptor').declare(DT.StringT);
  builder.name('right_ventricle.descriptor').declare(DT.StringT);
  builder.name('right_heart.descriptor').declare(DT.StringT);
  builder.name('pulmonary_artery.descriptor').declare(DT.StringT);
  builder.name('left_atrium.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('left_ventricle.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('aorta.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('additional_device.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('additional_structure.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('calcification.descriptor').define(Algo.emptyMeshDescriptor());

  builder.name('left_atrium').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'left_atrium.descriptor');
  builder.name('left_ventricle').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'left_ventricle.descriptor');
  builder.name('right_atrium').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'right_atrium.descriptor');
  builder.name('right_ventricle').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'right_ventricle.descriptor');
  builder.name('right_heart').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'right_heart.descriptor');
  builder.name('aorta').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'aorta.descriptor');
  builder.name('pulmonary_artery').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'pulmonary_artery.descriptor');
  builder.name('additional_device').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'additional_device.descriptor');
  builder.name('additional_structure').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'additional_structure.descriptor');
  builder.name('calcification').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'calcification.descriptor');

  builder.name('pt_svc11_imported').declare(DT.Point);
  builder.name('pt_svc12_imported').declare(DT.Point);
  builder.name('pt_svc13_imported').declare(DT.Point);
  builder.name('pt_ivc11_imported').declare(DT.Point);
  builder.name('pt_ivc12_imported').declare(DT.Point);
  builder.name('pt_ivc13_imported').declare(DT.Point);

  builder.name('pts_commissure_spline_imported').declare(DT.Polyline);

  builder.name('pts_landing_zone_imported').declare(DT.Polyline);

  builder.name('pts_eustachian_ridge_imported').define(Algo.emptyPolyline());

  builder.name('var_annulus_perimeter_for_sizing_provided').declare(DT.NumberT);

  builder.name('world_top_direction').define(new DT.Vector(new THREE.Vector3(0, 0, 1)));
  builder.name('world_bottom_direction').define(new DT.Vector(new THREE.Vector3(0, 0, -1)));
  builder.name('world_posterior_direction').define(new DT.Vector(new THREE.Vector3(0, 1, 0)));
  builder.name('world_anterior_direction').define(new DT.Vector(new THREE.Vector3(0, -1, 0)));
  builder.name('world_left_direction').define(new DT.Vector(new THREE.Vector3(1, 0, 0)));
  builder.name('world_right_direction').define(new DT.Vector(new THREE.Vector3(-1, 0, 0)));

  builder
    .name('body_orientation')
    .bind(Algo.makeOrientationTop, DT.Orientation, 'world_top_direction', 'world_posterior_direction');

  builder
    .name('pl_svc1')
    .bind(Algo.planeFromThreePoints, DT.Plane, 'pt_svc11_imported', 'pt_svc12_imported', 'pt_svc13_imported');
  builder
    .name('pl_ivc1')
    .bind(Algo.planeFromThreePoints, DT.Plane, 'pt_ivc11_imported', 'pt_ivc12_imported', 'pt_ivc13_imported');

  builder.name('right_atrium.triangles').bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'right_atrium');
  builder
    .name('right_ventricle.triangles')
    .bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'right_ventricle');
  builder.name('right_heart.triangles').bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'right_heart');
  builder.name('right_atrium.tree').bind(Algo.buildTriangleTree, DT.MatKDTree3d, 'right_atrium.triangles');
  builder.name('right_ventricle.tree').bind(Algo.buildTriangleTree, DT.MatKDTree3d, 'right_ventricle.triangles');
  builder.name('right_heart.tree').bind(Algo.buildTriangleTree, DT.MatKDTree3d, 'right_heart.triangles');

  builder
    .name('tp.pl_svc1')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder.name('tp.pl_svc1.dflt').bind(Algo.reference, DT.Plane, 'pl_svc1'),
      builder.name('tp.pl_svc1.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_svc1.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_svc1.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pl_ivc1')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder.name('tp.pl_ivc1.dflt').bind(Algo.reference, DT.Plane, 'pl_ivc1'),
      builder.name('tp.pl_ivc1.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_ivc1.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_ivc1.flag').define(Algo.defaultTuneFlag())
    );

  buildAreaMeasurement(builder, 'tp.pl_svc1', 'right_atrium.triangles', 'world_top_direction');
  buildAreaMeasurement(builder, 'tp.pl_ivc1', 'right_atrium.triangles', 'world_top_direction');

  builder.name('pts_commissure_spline').bind(Algo.closedPolyline, DT.Polyline, 'pts_commissure_spline_imported');
  builder
    .name('commissure_spline_area')
    .bind(Algo.areaMeasurement, DT.AreaMeasurement, 'pts_commissure_spline', 'world_left_direction');

  builder
    .name('tp.landing_zone_spline.pts')
    .bindRaw(
      Algo.tuneValue,
      DT.Polyline,
      builder
        .name('tp.landing_zone_spline.pts.dflt')
        .bind(Algo.closedPolyline, DT.Polyline, 'pts_landing_zone_imported'),
      builder.name('tp.landing_zone_spline.pts.user').define(Algo.emptyPolyline()),
      builder.name('tp.landing_zone_spline.pts.appr').define(Algo.emptyPolyline()),
      builder.name('tp.landing_zone_spline.pts.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.eustachian_ridge_spline.pts')
    .bindRaw(
      Algo.tuneValue,
      DT.Polyline,
      builder
        .name('tp.eustachian_ridge_spline.pts.dflt')
        .bind(Algo.openPolyline, DT.Polyline, 'pts_eustachian_ridge_imported'),
      builder.name('tp.eustachian_ridge_spline.pts.user').define(Algo.emptyPolyline()),
      builder.name('tp.eustachian_ridge_spline.pts.appr').define(Algo.emptyPolyline()),
      builder.name('tp.eustachian_ridge_spline.pts.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.ap_direction')
    .bindRaw(
      Algo.tuneValue,
      DT.Vector,
      builder.name('tp.ap_direction.dflt').bind(Algo.reference, DT.Vector, 'world_top_direction'),
      builder.name('tp.ap_direction.user').define(Algo.emptyVector()),
      builder.name('tp.ap_direction.appr').define(Algo.emptyVector()),
      builder.name('tp.ap_direction.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('landing_zone_area')
    .bind(
      Algo.areaMeasurement,
      DT.AreaMeasurement,
      'tp.landing_zone_spline.pts',
      'world_left_direction',
      'tp.ap_direction'
    );

  builder.name('pt_landing_zone_center').bind(Algo.getAreaMeasurementCenter, DT.Point, 'landing_zone_area');

  builder.name('pl_annulus').bind(Algo.getAreaMeasurementPlane, DT.Plane, 'landing_zone_area');

  builder.name('vec_landing_zone_normal').bind(Algo.normalOf, DT.Vector, 'pl_annulus');

  builder
    .name('var_landing_zone_area_perimeter')
    .bind(Algo.getAreaMeasurementPerimeter, DT.NumberT, 'landing_zone_area');

  builder
    .name('landing_zone_spline')
    .bind(Algo.makeSpline, DT.Spline, 'tp.landing_zone_spline.pts', 'landing_zone_area');

  builder.name('long_axis.points').bind(Algo.makeLongAxisPolylineTTVR, DT.Polyline, 'pl_annulus');
  builder.name('long_axis.centerline').bind(Algo.makeCenterline, DT.Centerline, 'long_axis.points');

  builder
    .name('heart_orientation')
    .bind(Algo.heartOrientationTTVR, DT.Orientation, 'landing_zone_area', 'world_top_direction');

  builder
    .name('bottom_up_orientation')
    .bind(Algo.makeOrientationTop, DT.Orientation, 'world_anterior_direction', 'world_top_direction');

  builder
    .name('en_face_orientation')
    .bindRaw(
      Algo.makeOrientationDir,
      DT.Orientation,
      builder.existing('world_top_direction'),
      builder.bind(Algo.invertVector, DT.Vector, 'vec_landing_zone_normal')
    );

  builder.name('device_roi').bind(Algo.getMeasurementROI, DT.RegionOfInterest, 'landing_zone_area');

  builder.name('basal_rv_translation').define(new DT.NumberT(5));

  builder
    .name('basal_rv_area_auto')
    .bindRaw(
      Algo.areaMeasurementRef,
      DT.AreaMeasurement,
      builder.bindRaw(
        Algo.sliceAortaWithPlane,
        DT.Polyline,
        builder.existing('right_heart.triangles'),
        builder.bind(Algo.translatePlane, DT.Plane, 'pl_annulus', 'basal_rv_translation')
      ),
      builder.existing('landing_zone_area')
    );
  builder.name('ln_basal_rv_ap_auto').bind(Algo.makeBasalRvApLine, DT.Line, 'basal_rv_area_auto');
  builder.name('ln_basal_rv_sl_auto').bind(Algo.makeBasalRvSlLine, DT.Line, 'basal_rv_area_auto');

  builder
    .name('tp.pt_basal_rv_a')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_basal_rv_a.dflt').bind(Algo.startOfTheLine, DT.Point, 'ln_basal_rv_ap_auto'),
      builder.name('tp.pt_basal_rv_a.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_a.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_a.flag').define(Algo.defaultTuneFlag())
    );
  builder
    .name('tp.pt_basal_rv_p')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_basal_rv_p.dflt').bind(Algo.endOfTheLine, DT.Point, 'ln_basal_rv_ap_auto'),
      builder.name('tp.pt_basal_rv_p.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_p.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_p.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('ln_basal_rv_ap').bind(Algo.lineBetweenPoints, DT.Line, 'tp.pt_basal_rv_a', 'tp.pt_basal_rv_p');

  builder
    .name('tp.pt_basal_rv_s')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_basal_rv_s.dflt').bind(Algo.startOfTheLine, DT.Point, 'ln_basal_rv_sl_auto'),
      builder.name('tp.pt_basal_rv_s.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_s.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_s.flag').define(Algo.defaultTuneFlag())
    );
  builder
    .name('tp.pt_basal_rv_l')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_basal_rv_l.dflt').bind(Algo.endOfTheLine, DT.Point, 'ln_basal_rv_sl_auto'),
      builder.name('tp.pt_basal_rv_l.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_l.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_l.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('ln_basal_rv_sl').bind(Algo.lineBetweenPoints, DT.Line, 'tp.pt_basal_rv_s', 'tp.pt_basal_rv_l');

  builder
    .name('basal_rv.roi')
    .bind(Algo.makeBasalRvRoi, DT.RegionOfInterest, 'ln_basal_rv_ap', 'ln_basal_rv_sl', 'basal_rv_area_auto');

  builder
    .name('var_basal_rv_ap')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLine, DT.NumberT, 'ln_basal_rv_ap'),
      builder.existing('ln_basal_rv_ap'),
      builder.existing('basal_rv.roi')
    );

  builder
    .name('var_basal_rv_sl')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLine, DT.NumberT, 'ln_basal_rv_sl'),
      builder.existing('ln_basal_rv_sl'),
      builder.existing('basal_rv.roi')
    );

  builder
    .name('tp.basal_rv_tv.translation')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder.name('tp.basal_rv_tv.translation.dflt').define(new DT.NumberT(10)),
      builder.name('tp.basal_rv_tv.translation.user').define(new DT.NumberT(0)),
      builder.name('tp.basal_rv_tv.translation.appr').define(new DT.NumberT(0)),
      builder.name('tp.basal_rv_tv.translation.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.basal_rv_tv.translation.range').define(new DT.RangeT([-10, 20]));

  builder
    .name('basal_rv_tv_area_auto')
    .bindRaw(
      Algo.areaMeasurementRef,
      DT.AreaMeasurement,
      builder.bindRaw(
        Algo.sliceAortaWithPlane,
        DT.Polyline,
        builder.existing('right_heart.triangles'),
        builder.bind(Algo.translatePlane, DT.Plane, 'pl_annulus', 'tp.basal_rv_tv.translation')
      ),
      builder.existing('landing_zone_area')
    );
  builder.name('ln_basal_rv_tv_ap_auto').bind(Algo.makeBasalRvApLine, DT.Line, 'basal_rv_tv_area_auto');
  builder.name('ln_basal_rv_tv_sl_auto').bind(Algo.makeBasalRvSlLine, DT.Line, 'basal_rv_tv_area_auto');

  builder
    .name('tp.pt_basal_rv_tv_a')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_basal_rv_tv_a.dflt').bind(Algo.startOfTheLine, DT.Point, 'ln_basal_rv_tv_ap_auto'),
      builder.name('tp.pt_basal_rv_tv_a.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_tv_a.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_tv_a.flag').define(Algo.defaultTuneFlag())
    );
  builder
    .name('tp.pt_basal_rv_tv_p')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_basal_rv_tv_p.dflt').bind(Algo.endOfTheLine, DT.Point, 'ln_basal_rv_tv_ap_auto'),
      builder.name('tp.pt_basal_rv_tv_p.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_tv_p.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_tv_p.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('ln_basal_rv_tv_ap').bind(Algo.lineBetweenPoints, DT.Line, 'tp.pt_basal_rv_tv_a', 'tp.pt_basal_rv_tv_p');

  builder
    .name('tp.pt_basal_rv_tv_s')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_basal_rv_tv_s.dflt').bind(Algo.startOfTheLine, DT.Point, 'ln_basal_rv_tv_sl_auto'),
      builder.name('tp.pt_basal_rv_tv_s.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_tv_s.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_tv_s.flag').define(Algo.defaultTuneFlag())
    );
  builder
    .name('tp.pt_basal_rv_tv_l')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_basal_rv_tv_l.dflt').bind(Algo.endOfTheLine, DT.Point, 'ln_basal_rv_tv_sl_auto'),
      builder.name('tp.pt_basal_rv_tv_l.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_tv_l.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_basal_rv_tv_l.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('ln_basal_rv_tv_sl').bind(Algo.lineBetweenPoints, DT.Line, 'tp.pt_basal_rv_tv_s', 'tp.pt_basal_rv_tv_l');

  builder
    .name('basal_rv_tv.roi')
    .bind(Algo.makeBasalRvRoi, DT.RegionOfInterest, 'ln_basal_rv_tv_ap', 'ln_basal_rv_tv_sl', 'basal_rv_tv_area_auto');

  builder
    .name('var_basal_rv_tv_ap')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLine, DT.NumberT, 'ln_basal_rv_tv_ap'),
      builder.existing('ln_basal_rv_tv_ap'),
      builder.existing('basal_rv_tv.roi')
    );

  builder
    .name('var_basal_rv_tv_sl')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLine, DT.NumberT, 'ln_basal_rv_tv_sl'),
      builder.existing('ln_basal_rv_tv_sl'),
      builder.existing('basal_rv_tv.roi')
    );

  builder
    .name('tp.pt_ra_height')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_ra_height.dflt')
        .bind(
          Algo.findMaxRaHeightPointTTVR,
          DT.Point,
          'right_atrium.tree',
          'right_atrium.triangles',
          'landing_zone_area',
          'world_right_direction'
        ),
      builder.name('tp.pt_ra_height.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_ra_height.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_ra_height.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_rv_depth')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_rv_depth.dflt')
        .bind(
          Algo.depthHeightPointTTVR,
          DT.Point,
          'right_ventricle.tree',
          'right_ventricle.triangles',
          'pl_annulus',
          'world_left_direction'
        ),
      builder.name('tp.pt_rv_depth.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_rv_depth.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_rv_depth.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_rv_depth_apex')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_rv_depth_apex.dflt')
        .bind(
          Algo.findApexPointTTVR,
          DT.Point,
          'right_ventricle.tree',
          'right_ventricle.triangles',
          'pl_annulus',
          'tp.ap_direction',
          'world_left_direction'
        ),
      builder.name('tp.pt_rv_depth_apex.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_rv_depth_apex.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_rv_depth_apex.flag').define(Algo.defaultTuneFlag())
    );

  const raHeightInfo = buildPointToPlaneDistanceMeasurementSignedInv(builder, 'tp.pt_ra_height', 'pl_annulus');

  builder
    .name('var_ra_height')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      raHeightInfo.distance,
      raHeightInfo.line,
      builder.bind(
        Algo.makeRaHeightRvDepthRoiTTVR,
        DT.RegionOfInterest,
        'tp.pt_ra_height',
        'tp.pt_rv_depth',
        'pl_annulus',
        'tp.ap_direction',
        'world_left_direction'
      ),
      builder.bindRaw(
        Algo.makeYesNoMaybeCriteria,
        DT.CheckCriteria,
        raHeightInfo.distance,
        builder.bindRaw(
          Algo.makeWithinCriteriaRange,
          DT.CriteriaRange,
          builder.define(new DT.NumberT(60)),
          builder.define(new DT.NumberT(100))
        ),
        builder.bindRaw(Algo.makeLessThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(50)))
      )
    );

  builder.name('var_ra_height_value').bind(Algo.getDistanceMeasurementValue, DT.NumberT, 'var_ra_height');

  const rvDepthInfo = buildPointToPlaneDistanceMeasurementSigned(builder, 'tp.pt_rv_depth', 'pl_annulus');

  builder
    .name('var_rv_depth')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      rvDepthInfo.distance,
      rvDepthInfo.line,
      builder.bind(
        Algo.makeRaHeightRvDepthRoiTTVR,
        DT.RegionOfInterest,
        'tp.pt_rv_depth',
        'tp.pt_ra_height',
        'pl_annulus',
        'tp.ap_direction',
        'world_left_direction'
      )
    );

  builder.name('var_rv_depth.roi').bind(Algo.getMeasurementROI, DT.RegionOfInterest, 'var_rv_depth');

  const rvDepthApexInfo = buildPointToPointDistanceMeasurement(
    builder,
    'tp.pt_rv_depth_apex',
    'pt_landing_zone_center'
  );

  builder
    .name('var_rv_depth_apex')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      rvDepthApexInfo.distance,
      rvDepthApexInfo.line,
      builder.bind(
        Algo.makeRaHeightRvDepthApexRoiTTVR,
        DT.RegionOfInterest,
        'tp.pt_rv_depth_apex',
        'tp.pt_ra_height',
        'pl_annulus',
        'tp.ap_direction',
        'world_left_direction'
      ),
      builder.bindRaw(
        Algo.makeYesNoCriteria,
        DT.CheckCriteria,
        rvDepthApexInfo.distance,
        builder.bindRaw(Algo.makeGreaterThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(30)))
      )
    );

  builder
    .name('var_ivc_to_tva')
    .bind(Algo.ivcToTvaDistanceTTVR, DT.DistanceMeasurement, 'tp.pl_ivc1.area', 'landing_zone_area');

  builder
    .name('var_ivc_to_tva_offset')
    .bindRaw(
      Algo.ivcToTvaOffsetDistanceTTVR,
      DT.DistanceMeasurement,
      builder.existing('tp.pl_ivc1.area'),
      builder.existing('landing_zone_area'),
      builder.bindRaw(Algo.makeLessThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(20))),
      builder.bindRaw(Algo.makeGreaterThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(25)))
    );

  builder
    .name('var_ivc_to_tva_offset_value')
    .bind(Algo.getDistanceMeasurementValue, DT.NumberT, 'var_ivc_to_tva_offset');

  const workingRoomInfo = builder.bindRaw(Algo.addNumbers, DT.NumberT, raHeightInfo.distance, rvDepthApexInfo.distance);

  builder
    .name('var_ra_rv_working_room')
    .bindRaw(
      Algo.makeValueMeasurement,
      DT.ValueMeasurement,
      workingRoomInfo,
      builder.bindRaw(
        Algo.makeYesNoCriteria,
        DT.CheckCriteria,
        workingRoomInfo,
        builder.bindRaw(Algo.makeGreaterThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(90)))
      )
    );

  builder
    .name('var_ra_rv_working_room_value')
    .bind(Algo.getValueMeasurementValue, DT.NumberT, 'var_ra_rv_working_room');

  builder
    .name('var_ra_volume')
    .bind(
      Algo.volumeOfRightAtriumTTVR,
      DT.NumberT,
      'right_atrium.triangles',
      'tp.pl_ivc1.area',
      'tp.pl_svc1.area',
      'world_top_direction'
    );

  builder.name('var_rv_volume').bind(Algo.volumeOfTheMesh, DT.NumberT, 'right_ventricle.triangles');

  // Device ------------------------------------------------------------------------------

  builder
    .name('tp.manufacturer')
    .bindRaw(
      Algo.tuneValue,
      DT.StringT,
      builder.name('tp.manufacturer.dflt').bind(Algo.getPreferredManufacturer, DT.StringT, 'instrumentation'),
      builder.name('tp.manufacturer.user').define(new DT.StringT('')),
      builder.name('tp.manufacturer.appr').define(new DT.StringT('')),
      builder.name('tp.manufacturer.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.manufacturer.list').bind(Algo.getAvailableManufacturers, DT.IdentifiersList, 'instrumentation');

  builder
    .name('instrumentation.manufacturer')
    .bind(Algo.getCardiacManufacturer, DT.CardiacManufacturer, 'instrumentation', 'tp.manufacturer');

  builder
    .name('tp.system')
    .bindRaw(
      Algo.tuneValue,
      DT.StringT,
      builder.name('tp.system.dflt').bind(Algo.getPreferredSystem, DT.StringT, 'instrumentation.manufacturer'),
      builder.name('tp.system.user').define(new DT.StringT('')),
      builder.name('tp.system.appr').define(new DT.StringT('')),
      builder.name('tp.system.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.system.list').bind(Algo.getAvailableSystems, DT.IdentifiersList, 'instrumentation.manufacturer');

  builder
    .name('instrumentation.system')
    .bind(Algo.getSystem, DT.CardiacSystem, 'instrumentation.manufacturer', 'tp.system');

  builder
    .name('tp.valveInsertionDepth')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder
        .name('tp.valveInsertionDepth.dflt')
        .bind(Algo.getValveInsertionDepth, DT.NumberT, 'instrumentation.system'),
      builder.name('tp.valveInsertionDepth.user').define(new DT.NumberT(0)),
      builder.name('tp.valveInsertionDepth.appr').define(new DT.NumberT(0)),
      builder.name('tp.valveInsertionDepth.flag').define(Algo.defaultTuneFlag())
    );
  builder
    .name('tp.valveInsertionDepth.range')
    .bind(Algo.getValveInsertionDepthRange, DT.RangeT, 'instrumentation.system');

  builder
    .name('tp.valveApTranslation')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder.name('tp.valveApTranslation.dflt').define(new DT.NumberT(0)),
      builder.name('tp.valveApTranslation.user').define(new DT.NumberT(0)),
      builder.name('tp.valveApTranslation.appr').define(new DT.NumberT(0)),
      builder.name('tp.valveApTranslation.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.valveApTranslation.range').define(new DT.RangeT([-10, 10]));

  builder
    .name('tp.valveSlTranslation')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder.name('tp.valveSlTranslation.dflt').define(new DT.NumberT(0)),
      builder.name('tp.valveSlTranslation.user').define(new DT.NumberT(0)),
      builder.name('tp.valveSlTranslation.appr').define(new DT.NumberT(0)),
      builder.name('tp.valveSlTranslation.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.valveSlTranslation.range').define(new DT.RangeT([-10, 10]));

  builder
    .name('tp.valveRotationAngleDeg')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder.name('tp.valveRotationAngleDeg.dflt').define(new DT.NumberT(0)),
      builder.name('tp.valveRotationAngleDeg.user').define(new DT.NumberT(0)),
      builder.name('tp.valveRotationAngleDeg.appr').define(new DT.NumberT(0)),
      builder.name('tp.valveRotationAngleDeg.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.valveRotationAngleDeg.range').define(new DT.RangeT([-180, 180]));

  builder
    .name('var_landing_zone_area_dP_value')
    .bind(Algo.getAreaMeasurementPerimeterDerivedDiameter, DT.NumberT, 'landing_zone_area');

  builder
    .name('var_landing_zone_area_dP')
    .bind(Algo.makeValueMeasurement, DT.ValueMeasurement, 'var_landing_zone_area_dP_value');

  builder
    .name('tp.valve')
    .bindRaw(
      Algo.tuneValue,
      DT.StringT,
      builder
        .name('tp.valve.dflt')
        .bindRaw(
          Algo.defaultValveSizeTTVR,
          DT.StringT,
          builder.existing('instrumentation.system'),
          builder.existing('var_annulus_perimeter_for_sizing_provided'),
          builder.define(new DT.NumberT(0.03))
        ),
      builder.name('tp.valve.user').define(new DT.StringT('')),
      builder.name('tp.valve.appr').define(new DT.StringT('')),
      builder.name('tp.valve.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.valve.list').bind(Algo.getAvailableValveDevicesTTVR, DT.IdentifiersList, 'instrumentation.system');

  builder.name('device.valve').bind(Algo.getValveDeviceTTVR, DT.TTVRValveDevice, 'instrumentation.system', 'tp.valve');

  builder.name('valve').bind(Algo.getDeviceModelAsync, DT.Mesh, 'device.valve');

  builder
    .name('valve.cs')
    .bind(
      Algo.defaultValvePositionTTVR,
      DT.TransformMatrix,
      'tp.valveInsertionDepth',
      'tp.valveApTranslation',
      'tp.valveSlTranslation',
      'tp.valveRotationAngleDeg',
      'pl_annulus',
      'tp.ap_direction'
    );

  builder.name('pl_device_bottom').bind(Algo.valveBottomPlaneTTVR, DT.Plane, 'valve.cs');

  builder.name('var_valve_diameter').bind(Algo.valveDiameterTTVR, DT.NumberT, 'device.valve');
  builder.name('var_valve_anchor_diameter').bind(Algo.valveAnchorDiameterTTVR, DT.NumberT, 'device.valve');

  const annulusOversizeInfo = builder.bind(
    Algo.calculateOversizeTTVR,
    DT.NumberT,
    'var_valve_diameter',
    'var_landing_zone_area_dP_value'
  );

  builder
    .name('var_annulus_oversize')
    .bindRaw(
      Algo.makeValueMeasurement,
      DT.ValueMeasurement,
      annulusOversizeInfo,
      builder.bindRaw(
        Algo.makeYesMaybeCriteria,
        DT.CheckCriteria,
        annulusOversizeInfo,
        builder.bindRaw(Algo.makeGreaterThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(0)))
      )
    );

  builder
    .name('tp.is_basal_rv_size_ap_based')
    .bindRaw(
      Algo.tuneValue,
      DT.BooleanT,
      builder.name('tp.is_basal_rv_size_ap_based.dflt').define(new DT.BooleanT(false)),
      builder.name('tp.is_basal_rv_size_ap_based.user').define(new DT.BooleanT(false)),
      builder.name('tp.is_basal_rv_size_ap_based.appr').define(new DT.BooleanT(false)),
      builder.name('tp.is_basal_rv_size_ap_based.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('var_basal_rv_size')
    .bind(Algo.getBasalRvSizeTTVR, DT.NumberT, 'var_basal_rv_ap', 'var_basal_rv_sl', 'tp.is_basal_rv_size_ap_based');

  const basalRvOversizeInfo = builder.bind(
    Algo.calculateOversizeTTVR,
    DT.NumberT,
    'var_valve_anchor_diameter',
    'var_basal_rv_size'
  );

  builder
    .name('var_basal_rv_oversize')
    .bindRaw(
      Algo.makeValueMeasurement,
      DT.ValueMeasurement,
      basalRvOversizeInfo,
      builder.bindRaw(
        Algo.makeYesMaybeCriteria,
        DT.CheckCriteria,
        basalRvOversizeInfo,
        builder.bindRaw(Algo.makeGreaterThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(0)))
      )
    );

  // Screen Classification ------------------------------------------------------------------------------
  // external dependencies
  builder.name('var_landing_zone_area_dP_value.endDiastole').declare(DT.NumberT);
  builder.name('var_ra_height_value.endSystole').declare(DT.NumberT);
  builder.name('var_ra_rv_working_room_value.endSystole').declare(DT.NumberT);
  builder.name('var_ra_rv_working_room_value.endDiastole').declare(DT.NumberT);
  builder.name('var_ivc_to_tva_offset_value.endSystole').declare(DT.NumberT);
  builder.name('var_papillary_distribution_value.endDiastole').declare(DT.NumberT);
  builder.name('var_papillary_heights_value.endDiastole').declare(DT.NumberT);

  // internal calculations
  builder.name('var_annulus_min_size').bind(Algo.getAreaMeasurementMinDiameter, DT.NumberT, 'landing_zone_area');
  builder.name('var_annulus_sl_size').bind(Algo.getAreaMeasurementXDiameter, DT.NumberT, 'landing_zone_area');
  builder.name('var_annulus_ap_size').bind(Algo.getAreaMeasurementYDiameter, DT.NumberT, 'landing_zone_area');
  builder.name('var_basal_rv_sl_size').bind(Algo.getDistanceMeasurementValue, DT.NumberT, 'var_basal_rv_sl');

  // internal dependencies
  builder.name('var_annulus_min_size.endDiastole').declare(DT.NumberT);
  builder.name('var_annulus_sl_size.endDiastole').declare(DT.NumberT);
  builder.name('var_annulus_ap_size.endDiastole').declare(DT.NumberT);
  builder.name('var_basal_rv_sl_size.endDiastole').declare(DT.NumberT);

  const annulusOversizeEd = builder.bind(
    Algo.calculateOversizeTTVR,
    DT.NumberT,
    'var_valve_diameter',
    'var_landing_zone_area_dP_value.endDiastole'
  );
  builder
    .name('var_landing_zone_device_oversize.lessThan.endDiastole')
    .bindRaw(Algo.isLessThan, DT.BooleanT, annulusOversizeEd, builder.define(new DT.NumberT(-0.03)));
  builder
    .name('var_landing_zone_device_oversize.withinRange1.endDiastole')
    .bindRaw(
      Algo.isWithinRange,
      DT.BooleanT,
      annulusOversizeEd,
      builder.define(new DT.NumberT(-0.03)),
      builder.define(new DT.NumberT(0.0))
    );
  builder
    .name('var_landing_zone_device_oversize.withinRange2.endDiastole')
    .bindRaw(
      Algo.isWithinRange,
      DT.BooleanT,
      annulusOversizeEd,
      builder.define(new DT.NumberT(0.0)),
      builder.define(new DT.NumberT(0.02))
    );

  builder
    .name('var_annulus_sl_size.greaterThan.endDiastole')
    .bind(Algo.isGreaterThan, DT.BooleanT, 'var_annulus_sl_size.endDiastole', 'var_valve_anchor_diameter');

  builder
    .name('var_annulus_ap_size.greaterThan.endDiastole')
    .bind(Algo.isGreaterThan, DT.BooleanT, 'var_annulus_ap_size.endDiastole', 'var_valve_anchor_diameter');

  const basalRvSlOversizeEd = builder.bind(
    Algo.calculateOversizeTTVR,
    DT.NumberT,
    'var_valve_anchor_diameter',
    'var_basal_rv_sl_size.endDiastole'
  );
  builder
    .name('var_basal_rv_sl_device_oversize.lessThan.endDiastole')
    .bindRaw(Algo.isLessThan, DT.BooleanT, basalRvSlOversizeEd, builder.define(new DT.NumberT(0.0)));
  builder
    .name('var_basal_rv_sl_device_oversize.greaterThan.endDiastole')
    .bindRaw(Algo.isGreaterThan, DT.BooleanT, basalRvSlOversizeEd, builder.define(new DT.NumberT(0.0)));

  builder
    .name('var_annulus_min_size.lessThan.endDiastole')
    .bindRaw(
      Algo.isLessThan,
      DT.BooleanT,
      builder.existing('var_annulus_min_size.endDiastole'),
      builder.define(new DT.NumberT(30.0))
    );

  builder
    .name('var_basal_rv_sl_size.lessThan.endDiastole')
    .bindRaw(
      Algo.isLessThan,
      DT.BooleanT,
      builder.existing('var_basal_rv_sl_size.endDiastole'),
      builder.define(new DT.NumberT(40.0))
    );

  builder
    .name('var_ra_height_value.lessThan.endSystole')
    .bindRaw(
      Algo.isLessThan,
      DT.BooleanT,
      builder.existing('var_ra_height_value.endSystole'),
      builder.define(new DT.NumberT(50.0))
    );

  builder
    .name('var_ra_rv_working_room_value.lessThan.endSystole')
    .bindRaw(
      Algo.isLessThan,
      DT.BooleanT,
      builder.existing('var_ra_rv_working_room_value.endSystole'),
      builder.define(new DT.NumberT(90.0))
    );

  builder
    .name('var_ra_rv_working_room_value.lessThan.endDiastole')
    .bindRaw(
      Algo.isLessThan,
      DT.BooleanT,
      builder.existing('var_ra_rv_working_room_value.endDiastole'),
      builder.define(new DT.NumberT(90.0))
    );

  builder
    .name('var_ivc_to_tva_offset_value.greaterThan.endSystole')
    .bindRaw(
      Algo.isGreaterThan,
      DT.BooleanT,
      builder.existing('var_ivc_to_tva_offset_value.endSystole'),
      builder.define(new DT.NumberT(25.0))
    );

  builder
    .name('var_papillary_distribution_value.lessThan.endDiastole')
    .bindRaw(
      Algo.isLessThan,
      DT.BooleanT,
      builder.existing('var_papillary_distribution_value.endDiastole'),
      builder.define(new DT.NumberT(3))
    );

  builder
    .name('var_papillary_heights_value.lessThan.endDiastole')
    .bindRaw(
      Algo.isLessThan,
      DT.BooleanT,
      builder.existing('var_papillary_heights_value.endDiastole'),
      builder.define(new DT.NumberT(3))
    );

  builder
    .name('var_is_anatomically_unsuitable')
    .bindRaw(
      Algo.logicalOr,
      DT.BooleanT,
      builder
        .name('var_annulus_is_too_large')
        .bindRaw(
          Algo.logicalOr,
          DT.BooleanT,
          builder.existing('var_landing_zone_device_oversize.lessThan.endDiastole'),
          builder.bindRaw(
            Algo.logicalAnd,
            DT.BooleanT,
            builder.existing('var_landing_zone_device_oversize.withinRange1.endDiastole'),
            builder.bindRaw(
              Algo.logicalOr,
              DT.BooleanT,
              builder.existing('var_basal_rv_sl_device_oversize.lessThan.endDiastole'),
              builder.bindRaw(
                Algo.logicalAnd,
                DT.BooleanT,
                builder.existing('var_basal_rv_sl_device_oversize.greaterThan.endDiastole'),
                builder.existing('var_annulus_sl_size.greaterThan.endDiastole'),
                builder.existing('var_annulus_ap_size.greaterThan.endDiastole')
              )
            )
          ),
          builder.bindRaw(
            Algo.logicalAnd,
            DT.BooleanT,
            builder.existing('var_landing_zone_device_oversize.withinRange2.endDiastole'),
            builder.existing('var_basal_rv_sl_device_oversize.lessThan.endDiastole'),
            builder.bindRaw(
              Algo.logicalOr,
              DT.BooleanT,
              builder.existing('var_papillary_distribution_value.lessThan.endDiastole'),
              builder.existing('var_papillary_heights_value.lessThan.endDiastole')
            )
          )
        ),
      builder
        .name('var_annulus_is_too_small')
        .bindRaw(Algo.logicalOr, DT.BooleanT, builder.existing('var_annulus_min_size.lessThan.endDiastole')),
      builder
        .name('var_basal_rv_sl_too_small')
        .bindRaw(Algo.logicalOr, DT.BooleanT, builder.existing('var_basal_rv_sl_size.lessThan.endDiastole')),
      builder
        .name('var_ra_height_too_small')
        .bindRaw(Algo.logicalOr, DT.BooleanT, builder.existing('var_ra_height_value.lessThan.endSystole')),
      builder
        .name('var_working_room_small')
        .bindRaw(
          Algo.logicalAnd,
          DT.BooleanT,
          builder.existing('var_ra_rv_working_room_value.lessThan.endSystole'),
          builder.existing('var_ra_rv_working_room_value.lessThan.endDiastole')
        ),
      builder
        .name('var_ivc_to_tva_offset_too_large')
        .bindRaw(Algo.logicalOr, DT.BooleanT, builder.existing('var_ivc_to_tva_offset_value.greaterThan.endSystole')),
      builder
        .name('var_other_unsuitable')
        .bindRaw(
          Algo.logicalOr,
          DT.BooleanT,
          builder.define(new DT.BooleanT(false)).name('var_is_other_anatomically_unsuitable')
        )
    );

  // Access ------------------------------------------------------------------------------

  builder.name('bones.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('arteries_kidneys.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('filter.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('hardware.descriptor').define(Algo.emptyMeshDescriptor());

  builder.name('bones').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'bones.descriptor');
  builder.name('arteries_kidneys').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'arteries_kidneys.descriptor');
  builder.name('filter').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'filter.descriptor');
  builder.name('hardware').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'hardware.descriptor');

  buildDeliveryAlgorithm(builder, 15, 28, 28);

  // Delivery --------------------------------------------------------------------------------
  builder
    .name('catheter.reference_cs')
    .bind(
      Algo.catheterReferenceCsTTVR,
      DT.TransformMatrix,
      'world_top_direction',
      'tp.catheterSize',
      'tp.pl_ivc1.area',
      'landing_zone_area'
    );

  builder.name('catheter.inter_joint_distance').define(new DT.NumberT(35));
  builder.name('catheter.tip_length').define(new DT.NumberT(78));
  builder.name('catheter.joint_curvature_radius').define(new DT.NumberT(13));
  builder.name('catheter.max_primary_rotation_angle_deg').define(new DT.NumberT(120));

  const catheterDefaults = builder.bind(
    Algo.catheterDefaultParamsTTVR,
    DT.CatheterDefaultParams,
    'catheter.reference_cs',
    'landing_zone_area',
    'catheter.inter_joint_distance',
    'catheter.max_primary_rotation_angle_deg'
  );

  builder
    .name('tp.catheterZTranslation')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder
        .name('tp.catheterZTranslation.dflt')
        .bindRaw(Algo.catheterInsertionDepthTTVR, DT.NumberT, catheterDefaults),
      builder.name('tp.catheterZTranslation.user').define(new DT.NumberT(0)),
      builder.name('tp.catheterZTranslation.appr').define(new DT.NumberT(0)),
      builder.name('tp.catheterZTranslation.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.catheterZTranslation.range').define(new DT.RangeT([-30, 30]));

  builder
    .name('tp.catheterZRotation')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder
        .name('tp.catheterZRotation.dflt')
        .bindRaw(Algo.catheterZRotationAngleDegTTVR, DT.NumberT, catheterDefaults),
      builder.name('tp.catheterZRotation.user').define(new DT.NumberT(0)),
      builder.name('tp.catheterZRotation.appr').define(new DT.NumberT(0)),
      builder.name('tp.catheterZRotation.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.catheterZRotation.range').define(new DT.RangeT([-90, 90]));

  builder
    .name('tp.catheterPrimaryRotation')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder
        .name('tp.catheterPrimaryRotation.dflt')
        .bindRaw(Algo.catheterPrimaryRotationAngleDegTTVR, DT.NumberT, catheterDefaults),
      builder.name('tp.catheterPrimaryRotation.user').define(new DT.NumberT(0)),
      builder.name('tp.catheterPrimaryRotation.appr').define(new DT.NumberT(0)),
      builder.name('tp.catheterPrimaryRotation.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.catheterPrimaryRotation.range').define(new DT.RangeT([0, 120]));

  builder
    .name('tp.catheterSecondaryRotation')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder
        .name('tp.catheterSecondaryRotation.dflt')
        .bindRaw(Algo.catheterSecondaryRotationAngleDegTTVR, DT.NumberT, catheterDefaults),
      builder.name('tp.catheterSecondaryRotation.user').define(new DT.NumberT(0)),
      builder.name('tp.catheterSecondaryRotation.appr').define(new DT.NumberT(0)),
      builder.name('tp.catheterSecondaryRotation.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.catheterSecondaryRotation.range').define(new DT.RangeT([-90, 90]));

  builder
    .name('catheter.path')
    .bind(
      Algo.makeCatheterPathTTVR,
      DT.CatheterPath,
      'right_atrium',
      'catheter.reference_cs',
      'tp.catheterPrimaryRotation',
      'tp.catheterSecondaryRotation',
      'tp.catheterZTranslation',
      'catheter.inter_joint_distance',
      'catheter.tip_length',
      'catheter.joint_curvature_radius'
    );

  builder
    .name('catheter')
    .bind(Algo.makeCatheterModelTTVR, DT.Mesh, 'catheter.path', 'tp.catheterSize', 'catheter.joint_curvature_radius');

  builder
    .name('catheter.cs')
    .bind(Algo.catheterPositionTTVR, DT.TransformMatrix, 'catheter.reference_cs', 'tp.catheterZRotation');

  builder
    .name('var_catheter_rotation_angle')
    .bind(Algo.catheterRotationAngleTTVR, DT.AngleMeasurement, 'catheter.cs', 'catheter.reference_cs');

  builder
    .name('var_catheter_secondary_flex')
    .bind(Algo.catheterSecondaryFlexTTVR, DT.AngleMeasurement, 'catheter.path', 'catheter.cs', 'catheter.reference_cs');

  builder
    .name('var_catheter_primary_flex')
    .bind(Algo.catheterPrimaryFlexTTVR, DT.AngleMeasurement, 'catheter.path', 'catheter.cs', 'catheter.reference_cs');

  builder
    .name('var_catheter_to_tv_angle')
    .bind(Algo.catheterToTVNormalAngleTTVR, DT.AngleMeasurement, 'catheter.path', 'catheter.cs', 'landing_zone_area');
}
